/* eslint-disable no-fallthrough */

import {
  addQueryParamsToUrl,
  generateUrl,
  updatePathParamsForApiUrl
} from '../utils/UrlGenerator'
import axios, { AxiosInstance, CancelTokenSource } from 'axios'

import { ClientEncryptionService } from './encryption/encryption.service'
import HttpConstants from '../constants/HttpConstants'
import StringConstants from '../constants/StringConstants'
import UrlConstants from '../constants/UrlConstants'
import cookie from 'react-cookies'
import { loadProgressBar } from 'x-axios-progress-bar'
import { rootStore } from '../store/StoreContext'
import routeConstants from '../constants/RouteConstants'

// Enhanced request tracking with better deduplication
const activeCancelTokens: Record<string, CancelTokenSource> = {}
const activeRequests: Record<
  string,
  {
    promise: Promise<any>
    isCompleted: boolean
    timestamp: number
    requestId: string
  }
> = {}
const latestRequestIds: Record<string, string> = {}
const activeUrlRequests: Record<string, string> = {}

// Add a request cache for identical GET requests
const requestCache: Record<
  string,
  {
    data: any
    timestamp: number
    expiry: number
  }
> = {}
const CACHE_DURATION = 300

function getSessionToken() {
  return localStorage.getItem('_grecaptcha') || null
}

const sessionToken = getSessionToken()
const delay = (ms: any) => new Promise((resolve) => setTimeout(resolve, ms))

// Enhanced request key generation with better hashing
function generateRequestKey(
  usecase: string,
  httpMethod: string,
  data: any,
  authenticatedCall: boolean,
  finalUrl?: string
): string {
  // Create a stable hash of the data
  const dataHash = data ? createDataHash(data) : 'no-data'
  const baseKey = `${usecase}:${httpMethod}:${authenticatedCall}:${dataHash}`
  return finalUrl ? `${baseKey}:${encodeURIComponent(finalUrl)}` : baseKey
}

// Create a stable hash for data objects
function createDataHash(data: any): string {
  if (!data || typeof data !== 'object') {
    return String(data)
  }

  const sortedKeys = Object.keys(data).sort()
  const sortedData: any = {}
  sortedKeys.forEach((key) => {
    sortedData[key] = data[key]
  })

  return JSON.stringify(sortedData)
}

function generateUniqueRequestId(): string {
  return `${Date.now()}_${Math.random().toString(36).substring(2, 15)}`
}

function createFinalUrl(
  baseUrl: string,
  params: any,
  httpMethod: string
): string {
  if (
    httpMethod === HttpConstants.GET_METHOD ||
    httpMethod === HttpConstants.DELETE_METHOD
  ) {
    return addQueryParamsToUrl(baseUrl, params, httpMethod)
  }
  return baseUrl
}

// Check if we have a cached response for GET requests
function getCachedResponse(requestKey: string, httpMethod: string): any | null {
  if (httpMethod !== HttpConstants.GET_METHOD) {
    return null
  }

  const cached = requestCache[requestKey]
  if (!cached) {
    return null
  }

  const now = Date.now()
  if (now - cached.timestamp > cached.expiry) {
    delete requestCache[requestKey]
    return null
  }

  return cached.data
}

// Cache response for GET requests
function cacheResponse(
  requestKey: string,
  httpMethod: string,
  data: any
): void {
  if (httpMethod !== HttpConstants.GET_METHOD) {
    return
  }

  requestCache[requestKey] = {
    data: data,
    timestamp: Date.now(),
    expiry: CACHE_DURATION
  }
}

// Clean expired cache entries
function cleanExpiredCache(): void {
  const now = Date.now()
  Object.keys(requestCache).forEach((key) => {
    const cached = requestCache[key]
    if (now - cached.timestamp > cached.expiry) {
      delete requestCache[key]
    }
  })
}

function getSecureApiClient(
  token: string,
  cancelTokenSource?: CancelTokenSource,
  options?: {
    disableEncryption?: boolean
    requestId?: string
    requestKey?: string
  }
): AxiosInstance {
  const client = axios.create()
  client.defaults.headers.common['Authorization'] = token
    ? `Bearer ${token}`
    : ''
  // client.defaults.headers.common['x-encrypted'] =
  //   options?.disableEncryption === true ? false : true
  client.defaults.headers.common['Request-id'] =
    generateRandom10DigitAlphanumericString()

  if (options?.requestId) {
    client.defaults.headers.common['x-request-id'] = options.requestId
  }

  if (sessionToken) {
    client.defaults.headers.common['Session-id'] = sessionToken
  } else {
    client.defaults.headers.common['Session-id'] = ''
  }
  if (cancelTokenSource) {
    client.defaults.cancelToken = cancelTokenSource.token
  }
  loadProgressBar({}, client as any)

  client.interceptors.request.use(
    async (config) => {
      config.headers = config.headers || {}

      if (api.getIsKeyExchange()) {
        await delay(500)
      }

      if (!api.getClientId() || !api.getServerPublicKey()) {
        await api.initialize()
      }

      if (config.data) {
        config.data = await api.encryptRequest(config.data)
      }

      config.headers['x-client-id'] = api.getClientId() || ''

      return config
    },
    (error) => Promise.reject(error)
  )

  client.interceptors.response.use(
    async (response) => {
      const headers = response.config.headers || {}
      const requestId = headers['x-request-id']
      const requestKey = options?.requestKey

      // Enhanced stale response check
      if (
        requestKey &&
        requestId &&
        latestRequestIds[requestKey] &&
        latestRequestIds[requestKey] !== requestId
      ) {
        return Promise.reject(new Error('Stale response ignored'))
      }
      const disableEncryption = options?.disableEncryption === true

      if (disableEncryption) {
        return response
      }
      if (response.data) {
        response.data = await api.decryptResponse(response.data)
      }
      return response
    },
    (error) => {
      if (axios.isCancel(error)) {
        return Promise.reject(error)
      }

      const headers = error.config?.headers || {}
      const requestId = headers['x-request-id']
      const requestKey = options?.requestKey

      if (
        requestKey &&
        requestId &&
        latestRequestIds[requestKey] &&
        latestRequestIds[requestKey] !== requestId
      ) {
        return Promise.reject(new Error('Stale error ignored'))
      }

      return Promise.reject(error)
    }
  )
  return client
}

function generateRandom10DigitAlphanumericString(): string {
  return Math.random().toString(36).substring(2, 12).toUpperCase()
}

const api = new ClientEncryptionService()

function cancelPreviousRequest(requestKey: string): void {
  if (activeCancelTokens[requestKey]) {
    activeCancelTokens[requestKey].cancel(
      `Cancelled duplicate API call for ${requestKey}`
    )
    delete activeCancelTokens[requestKey]
  }
  if (activeRequests[requestKey]) {
    activeRequests[requestKey].isCompleted = true
    delete activeRequests[requestKey]
  }

  // Clean up URL tracking
  Object.keys(activeUrlRequests).forEach((url) => {
    if (activeUrlRequests[url] === requestKey) {
      delete activeUrlRequests[url]
    }
  })
}

function cleanupRequest(requestKey: string, finalUrl?: string): void {
  if (activeCancelTokens[requestKey]) {
    delete activeCancelTokens[requestKey]
  }
  if (activeRequests[requestKey]) {
    activeRequests[requestKey].isCompleted = true
    delete activeRequests[requestKey]
  }

  if (finalUrl && activeUrlRequests[finalUrl]) {
    delete activeUrlRequests[finalUrl]
  }

  // Keep the latest request ID for handling delayed responses
  setTimeout(() => {
    if (latestRequestIds[requestKey]) {
      delete latestRequestIds[requestKey]
    }
  }, 3000)
}

function makeApiCallWithAuthentication(
  usecase: string,
  httpMethod: string,
  data: any,
  options?: {
    disableEncryption?: boolean
    signal?: AbortSignal
    allowDuplicates?: boolean
    bypassCache?: boolean
  }
) {
  return makeApiCallInternal(usecase, httpMethod, true, data, options)
}

function makeApiCall(
  usecase: string,
  httpMethod: string,
  data: any,
  options?: {
    disableEncryption?: boolean
    allowDuplicates?: boolean
    bypassCache?: boolean
  }
) {
  return makeApiCallInternal(usecase, httpMethod, false, data, options)
}

function makeApiCallInternal(
  usecase: string,
  httpMethod: string,
  authenticatedCall: boolean,
  data: any,
  options?: {
    disableEncryption?: boolean
    allowDuplicates?: boolean
    bypassCache?: boolean
  }
) {
  // Clean expired cache periodically
  if (Math.random() < 0.1) {
    // 10% chance to clean cache
    cleanExpiredCache()
  }
  const originalData = { ...data }

  const { url: initialUrl, params } = updatePathParamsForApiUrl(
    generateUrl(usecase) as string,
    data
  )
  let finalParams = params
  if (
    httpMethod === HttpConstants.DELETE_METHOD &&
    (!params || Object.keys(params).length === 0) &&
    originalData &&
    Object.keys(originalData).length > 0
  ) {
    finalParams = originalData
  }

  const finalUrl = createFinalUrl(initialUrl, finalParams, httpMethod)
  const requestKey = generateRequestKey(
    usecase,
    httpMethod,
    originalData,
    authenticatedCall,
    finalUrl
  )

  // Check cache first for GET requests
  if (!options?.bypassCache && httpMethod === HttpConstants.GET_METHOD) {
    const cachedResponse = getCachedResponse(requestKey, httpMethod)
    if (cachedResponse) {
      return Promise.resolve(cachedResponse)
    }
  }

  const requestId = generateUniqueRequestId()

  // Enhanced duplicate prevention
  if (!options?.allowDuplicates) {
    // Check by URL first (most precise)
    if (activeUrlRequests[finalUrl]) {
      const existingRequestKey = activeUrlRequests[finalUrl]
      const existingRequest = activeRequests[existingRequestKey]
      if (existingRequest && !existingRequest.isCompleted) {
        return existingRequest.promise
      }
    }

    // Check by request key
    const existingRequest = activeRequests[requestKey]
    if (existingRequest && !existingRequest.isCompleted) {
      return existingRequest.promise
    }

    // Cancel any previous request
    cancelPreviousRequest(requestKey)
  }

  // Set this as the latest request
  latestRequestIds[requestKey] = requestId
  activeUrlRequests[finalUrl] = requestKey

  const CancelToken = axios.CancelToken
  const source = CancelToken.source()
  activeCancelTokens[requestKey] = source

  const token = authenticatedCall
    ? cookie.load(StringConstants.COOKIE_TOKEN)
    : ''
  const secureClient = getSecureApiClient(token, source, {
    ...options,
    requestId,
    requestKey
  })

  // Enhanced error handling
  secureClient.interceptors.response.use(
    (response) => response,
    (error) => {
      if (axios.isCancel(error)) {
        return Promise.reject(error)
      }

      if (
        error.message === 'Stale response ignored' ||
        error.message === 'Stale error ignored'
      ) {
        return Promise.reject(new Error('Request superseded by newer request'))
      }

      if (
        error.response?.status === 401 &&
        !error.response.request.responseURL.includes('/things-to-do') &&
        !error.response.request.responseURL.includes('/login') &&
        !error.response.request.responseURL.includes('/member') &&
        !error.response.request.responseURL.includes('/project') &&
        !error.response.request.responseURL.includes('/details')
      ) {
        window.location.href = routeConstants.UNAUTHORIZED_PAGE
      }
      return Promise.reject(error)
    }
  )

  const scope = rootStore.scopeStore.getScope()
  rootStore.uiStore.setAxiosSource(source)
  const scopeName = UrlConstants[usecase].SCOPE
  let headers: any = { headers: {} }

  if (authenticatedCall) {
    headers = {
      headers: {
        Authorization: 'Bearer ' + cookie.load(StringConstants.COOKIE_TOKEN)
      }
    }
  }

  switch (scopeName) {
    //noFallthroughCasesInSwitch flag in tsconfig.json is set to  false to suppress the error
    //TODO: localize error suppression to this block and set noFallthroughCasesInSwitch flag to true
    case 'environment':
      headers.headers['environment-id'] = scope['environment-id']
    case 'project':
      headers.headers['project-id'] = scope['project-id']
    case 'organization':
      headers.headers['organization-id'] = scope['organization-id']
      break
    default:
  }
  const workingData = { ...originalData }

  // in case of create environment, do not use project id from global state
  // since environment can be created for any project in settings
  if (
    [
      UrlConstants.CREATE_ENVIRONMENT.USECASE,
      UrlConstants.FIND_ALL_ENVIRONMENTS.USECASE,
      UrlConstants.CREATE_USER_PROJECT_INVITE.USECASE,
      UrlConstants.FIND_PROJECT.USECASE,
      UrlConstants.DELETE_PROJECT.USECASE,
      UrlConstants.GET_PROJECT_MEMBER_FILTERS.USECASE,
      UrlConstants.FIND_PROJECT_MEMBERS.USECASE,
      UrlConstants.REMOVE_PROJECT_MEMBER.USECASE,
      UrlConstants.UPDATE_PROJECT_MEMBER.USECASE,
      UrlConstants.UPDATE_PROJECT.USECASE,
      UrlConstants.ADD_PROJECT_MEMBER.USECASE,
      UrlConstants.ENVIRONMENT_NAME_AVAILABILITY.USECASE,
      UrlConstants.UNASSIGNED_ORGANIZATION_MEMBER_LOOK_UP_FOR_PROJECT.USECASE,
      UrlConstants.DISCONNECT_PROJECT_TO_SLACK.USECASE
    ].includes(usecase)
  ) {
    headers.headers['project-id'] = workingData.projectId
    delete workingData.projectId
  }

  if (usecase === UrlConstants.ON_BOARD_USER_VIA_SSO.USECASE) {
    headers.headers['code'] = workingData.code
    delete workingData.code
  }

  if (
    [
      UrlConstants.FIND_ENVIRONMENT.USECASE,
      UrlConstants.UPDATE_ENVIRONMENT.USECASE,
      UrlConstants.DELETE_ENVIRONMENT.USECASE,
      UrlConstants.GET_ENVIRONMENT_FILTER.USECASE
    ].includes(usecase)
  ) {
    headers.headers['project-id'] = workingData.projectId
    headers.headers['environment-id'] = workingData.environmentId
    delete workingData.projectId
    delete workingData.environmentId
  }

  if (
    [
      UrlConstants.GET_HEALTH_SEARCH_CONTEXTS.USECASE,
      UrlConstants.GET_EXCEPTION_SEARCH_CONTEXTS.USECASE,
      UrlConstants.GET_JOB_SEARCH_CONTEXTS
    ].includes(usecase)
  ) {
    if (workingData.includeCancelToken) {
      headers.cancelToken = source.token
      delete workingData.includeCancelToken
    }
  }

  if (usecase === UrlConstants.CONNECT_TO_SALESFORCE.USECASE) {
    headers.headers['code'] = workingData.code
    headers.headers['environment-id'] = workingData.headers?.environmentId
    headers.headers['project-id'] = workingData.headers?.projectId
    headers.headers['organization-id'] = workingData.headers?.organizationId
  }

  let requestPromise: Promise<any>
  let url = initialUrl

  const handleError = (err: any) => {
    if (
      err.message === 'Stale response ignored' ||
      err.message === 'Stale error ignored'
    ) {
      return Promise.reject(new Error('Request superseded by newer request'))
    }
    if (axios.isCancel(err)) {
      return Promise.reject(err)
    }
    throw err.response || err
  }

  // Make the actual HTTP request
  switch (httpMethod) {
    case HttpConstants.GET_METHOD:
      if (finalParams) {
        url = addQueryParamsToUrl(url, finalParams, HttpConstants.GET_METHOD)
      }
      requestPromise = secureClient.get(url, headers).catch(handleError)
      break

    case HttpConstants.POST_METHOD:
      if (usecase === UrlConstants.REGISTER_USER.USECASE) {
        url = addQueryParamsToUrl(url, finalParams, HttpConstants.POST_METHOD)
      }
      requestPromise = secureClient
        .post(url, finalParams, headers)
        .catch(handleError)
      break

    case HttpConstants.PATCH_METHOD:
      if (
        [
          UrlConstants.UPDATE_ORGANIZATION_MEMBER.USECASE,
          UrlConstants.UPDATE_PROJECT_MEMBER.USECASE,
          UrlConstants.UPDATE_EXCEPTION_GROUP.USECASE
        ].includes(usecase)
      ) {
        url = addQueryParamsToUrl(url, finalParams, HttpConstants.PATCH_METHOD)
      }
      requestPromise = secureClient
        .patch(url, finalParams, headers)
        .catch(handleError)
      break

    case HttpConstants.DELETE_METHOD: {
      const deleteParams =
        Object.keys(workingData).length > 0 ? workingData : finalParams
      if (deleteParams) {
        url = addQueryParamsToUrl(url, deleteParams)
      }
      requestPromise = secureClient
        .delete(url, { data: deleteParams, ...headers })
        .catch(handleError)
      break
    }

    default:
      cleanupRequest(requestKey, finalUrl)
      throw new Error('No HTTP Method found')
  }

  // Enhanced promise handling with caching
  const enhancedPromise = requestPromise
    .then((response) => {
      // Cache successful GET responses
      if (httpMethod === HttpConstants.GET_METHOD && response.data) {
        cacheResponse(requestKey, httpMethod, response)
      }
      return response
    })
    .finally(() => {
      cleanupRequest(requestKey, finalUrl)
    })

  const requestWrapper = {
    promise: enhancedPromise,
    isCompleted: false,
    timestamp: Date.now(),
    requestId: requestId
  }

  activeRequests[requestKey] = requestWrapper
  return requestWrapper.promise
}

// Enhanced utility functions
const clearStaleTrackingData = () => {
  Object.keys(activeCancelTokens).forEach(
    (key) => delete activeCancelTokens[key]
  )
  Object.keys(activeRequests).forEach((key) => delete activeRequests[key])
  Object.keys(latestRequestIds).forEach((key) => delete latestRequestIds[key])
  Object.keys(activeUrlRequests).forEach((key) => delete activeUrlRequests[key])
  Object.keys(requestCache).forEach((key) => delete requestCache[key])
}

// Initialize clean state
clearStaleTrackingData()

const cancelRequest = (
  usecase: string,
  httpMethod: string,
  data: any,
  authenticatedCall = false
) => {
  const { url, params } = updatePathParamsForApiUrl(
    generateUrl(usecase) as string,
    data
  )
  const finalUrl = createFinalUrl(url, params, httpMethod)
  const requestKey = generateRequestKey(
    usecase,
    httpMethod,
    data,
    authenticatedCall,
    finalUrl
  )
  cancelPreviousRequest(requestKey)
}

const cancelAllRequests = () => {
  Object.keys(activeCancelTokens).forEach((requestKey) => {
    activeCancelTokens[requestKey].cancel('Cancelled all requests')
  })
  clearStaleTrackingData()
}

const getActiveRequestsCount = () => {
  return Object.values(activeRequests).filter((req) => !req.isCompleted).length
}

const clearStaleRequests = (maxAgeMs = 30000) => {
  const now = Date.now()
  Object.keys(activeRequests).forEach((requestKey) => {
    const request = activeRequests[requestKey]
    if (now - request.timestamp > maxAgeMs) {
      cancelPreviousRequest(requestKey)
    }
  })
}

const isRequestActive = (
  usecase: string,
  httpMethod: string,
  data: any,
  authenticatedCall = false
) => {
  const { url, params } = updatePathParamsForApiUrl(
    generateUrl(usecase) as string,
    data
  )
  const finalUrl = createFinalUrl(url, params, httpMethod)
  const requestKey = generateRequestKey(
    usecase,
    httpMethod,
    data,
    authenticatedCall,
    finalUrl
  )
  return activeRequests[requestKey] && !activeRequests[requestKey].isCompleted
}

// New utility to clear cache for specific patterns
const clearCacheForPattern = (pattern: string) => {
  Object.keys(requestCache).forEach((key) => {
    if (key.includes(pattern)) {
      delete requestCache[key]
    }
  })
}

export default {
  makeApiCallWithAuthentication,
  makeApiCall,
  cancelRequest,
  cancelAllRequests,
  getActiveRequestsCount,
  clearStaleRequests,
  isRequestActive,
  clearStaleTrackingData,
  clearCacheForPattern
}
